import { Component, type ErrorInfo, type ReactNode } from 'react';

import { captureException } from '../lib/error-tracker.js';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: Error | null;
}

/**
 * Top-level render-error boundary. Catches errors thrown during render,
 * reports them via `captureException`, and shows a minimal fallback so the
 * whole app doesn't go blank.
 *
 * Does NOT catch: event handlers, async code, SSR, errors during setState
 * inside lifecycle methods of the boundary itself — use `captureException`
 * directly for those.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    captureException(error, { componentStack: info.componentStack });
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null });
  };

  public override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          className="mx-auto flex min-h-screen max-w-xl flex-col items-start justify-center gap-4 p-8"
        >
          <h1 className="text-2xl font-semibold">Something went wrong.</h1>
          <p className="text-muted-foreground text-sm">
            The error has been logged. You can try reloading this view.
          </p>
          <pre className="bg-muted max-w-full overflow-auto rounded-md p-3 text-xs">
            {this.state.error.message}
          </pre>
          <button
            type="button"
            onClick={this.handleReset}
            className="bg-primary text-primary-foreground inline-flex items-center rounded-md px-4 py-2 text-sm font-medium"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
